import type {
  ReferenceUiSession,
  ReferenceUiSessionStep,
} from './types';
import type { ReferenceImage } from '@/components/MessageList/ReferenceUIMessage/const';
import { ReferenceUiSessionStep as SessionStep } from './types';

const MAX_PERSISTED_REFERENCE_IMAGE_URL_LENGTH = 16 * 1024;
const MAX_PERSISTED_REFERENCE_TEXT_LENGTH = 2 * 1024;

export interface SetImagesOptions {
  open?: boolean;
  loading?: boolean;
  messageId?: string;
  sourceUpdatedAt?: number;
}

function compactPersistedText(value?: string, maxLength = MAX_PERSISTED_REFERENCE_TEXT_LENGTH) {
  return value?.slice(0, maxLength);
}

function compactReferenceImageForPersist(image: ReferenceImage): ReferenceImage | undefined {
  const url = image.url.trim();
  if (
    !url
    || url.length > MAX_PERSISTED_REFERENCE_IMAGE_URL_LENGTH
    || /^(?:data|blob):/i.test(url)
  ) {
    return undefined;
  }

  return {
    url,
    styleName: compactPersistedText(image.styleName),
    styleDisplayName: compactPersistedText(image.styleDisplayName),
    styleDescription: compactPersistedText(image.styleDescription),
    description: compactPersistedText(image.description),
    isRecommended: image.isRecommended,
    recommendReason: compactPersistedText(image.recommendReason),
    index: image.index,
    total: image.total,
  };
}

export function compactReferenceUiSessionForPersist(
  session: ReferenceUiSession,
): ReferenceUiSession | undefined {
  const images = session.images.map(compactReferenceImageForPersist);
  if (images.length === 0 || images.some(image => !image)) {
    return undefined;
  }

  const compactImages = images as ReferenceImage[];
  const selectedImage = session.selectedImage
    ? compactImages.find(image => image.url === session.selectedImage?.url)
    : undefined;

  return {
    images: compactImages,
    messageId: compactPersistedText(session.messageId, 512),
    imagesUpdatedAt: session.imagesUpdatedAt,
    selectedImage,
    sidePanelOpen: session.sidePanelOpen,
    step: selectedImage ? session.step : SessionStep.StyleSelect,
    regenerateAllReferenceUsed: session.regenerateAllReferenceUsed,
  };
}

type RestoredStep = Extract<
  ReferenceUiSessionStep,
  SessionStep.StyleSelect | SessionStep.ReferenceFeedback | SessionStep.ReferenceFeedbackConfirmed
>;

function hasImage(images: ReferenceImage[], image?: ReferenceImage): boolean {
  if (!image?.url) {
    return false;
  }
  return images.some(item => item.url === image.url);
}

function getImageSignature(image?: ReferenceImage): string {
  if (!image) {
    return '';
  }

  return [
    image.url || '',
    image.styleName || '',
    image.styleDisplayName || '',
    image.styleDescription || '',
    image.recommendReason || '',
    image.isRecommended ? '1' : '0',
  ].join('|');
}

function areSameImages(a: ReferenceImage[], b: ReferenceImage[]): boolean {
  if (a.length !== b.length) {
    return false;
  }

  return a.every((image, index) => getImageSignature(image) === getImageSignature(b[index]));
}

function areSameImage(a?: ReferenceImage, b?: ReferenceImage): boolean {
  if (!a || !b) {
    return !a && !b;
  }

  return getImageSignature(a) === getImageSignature(b);
}

function findImageByUrl(images: ReferenceImage[], imageUrl?: string): ReferenceImage | undefined {
  if (!imageUrl) {
    return undefined;
  }
  return images.find(image => image.url === imageUrl);
}

function getRestoredStep(
  persistedStep: ReferenceUiSessionStep | undefined,
  selectedImage: ReferenceImage | undefined,
): RestoredStep {
  if (persistedStep === SessionStep.ReferenceFeedbackConfirmed && selectedImage?.url) {
    return persistedStep;
  }

  if (selectedImage?.url) {
    return SessionStep.ReferenceFeedback;
  }

  return SessionStep.StyleSelect;
}

export function buildSessionFromReferenceImages(
  existing: ReferenceUiSession | undefined,
  images: ReferenceImage[],
  options?: SetImagesOptions,
): ReferenceUiSession | null {
  const hasIncomingImages = images.length > 0;
  const hasIncomingGeneratedImage = images.some(image => Boolean(image.url));
  const incomingImagesUpdatedAt = options?.sourceUpdatedAt;
  const existingImagesUpdatedAt = existing?.imagesUpdatedAt;
  const messageId = options?.messageId ?? existing?.messageId;
  const sidePanelOpen = options?.open ?? existing?.sidePanelOpen ?? false;

  // 检测过期传入：existing 比传入的更新，丢弃传入的图片
  const isStaleIncomingImageSet = Boolean(
    existing &&
    hasIncomingImages &&
    hasIncomingGeneratedImage &&
    !areSameImages(existing.images, images) &&
    typeof incomingImagesUpdatedAt === 'number' &&
    typeof existingImagesUpdatedAt === 'number' &&
    incomingImagesUpdatedAt < existingImagesUpdatedAt,
  );

  if (isStaleIncomingImageSet) {
    if (existing!.sidePanelOpen === sidePanelOpen) {
      return null;
    }
    return { ...existing!, sidePanelOpen };
  }

  // 选择基础图片列表
  const baseImages = hasIncomingImages ? images : [];
  const hasGeneratedImage = baseImages.some(image => Boolean(image.url));
  const selectedImage = hasImage(baseImages, existing?.selectedImage)
    ? findImageByUrl(baseImages, existing?.selectedImage?.url) ?? existing?.selectedImage
    : undefined;
  const isNewImageSet = Boolean(existing && !areSameImages(existing.images, baseImages));
  const isIncomingNewImageSet = isNewImageSet && hasIncomingImages && hasGeneratedImage;
  const restoredStep = getRestoredStep(existing?.step, selectedImage);
  const step = existing?.step ?? restoredStep;
  const imagesUpdatedAt = isIncomingNewImageSet || (!existing && hasGeneratedImage)
    ? (incomingImagesUpdatedAt ?? Date.now())
    : existingImagesUpdatedAt;

  if (
    existing &&
    areSameImages(existing.images, baseImages) &&
    existing.messageId === messageId &&
    areSameImage(existing.selectedImage, selectedImage) &&
    existing.sidePanelOpen === sidePanelOpen &&
    existing.step === step
  ) {
    return null;
  }

  const regenerateAllReferenceUsed = existing?.regenerateAllReferenceUsed;

  return {
    images: baseImages,
    messageId,
    imagesUpdatedAt,
    selectedImage,
    sidePanelOpen,
    regenerateAllReferenceUsed: isIncomingNewImageSet ? undefined : regenerateAllReferenceUsed,
    ...(isNewImageSet
      ? {
          step: SessionStep.StyleSelect,
          selectedImage: undefined,
        }
      : {
          step,
        }),
  };
}
